/**
 * Local skip-quota tracker for non-VIP listeners on the Music / AI /
 * Beats radios. Allows up to N skips per ROLLING WINDOW, then locks
 * for the remainder of the window before resetting.
 *
 * Behaviour the user asked for:
 *   • Default: 3 skips per 15 minutes
 *   • Listener must use ALL 3 before the 15-min cooldown timer starts
 *   • Once timer is running, no more skips allowed until reset
 *   • Resets back to 3 skips when the timer completes
 *
 * State persists in `localStorage` keyed by station so refreshing the
 * page doesn't reset the quota. State is intentionally client-side
 * because skips are local seeks — they don't affect other listeners.
 */
import { useEffect, useState, useCallback } from 'react';

export const SKIP_QUOTA = 3;
export const SKIP_WINDOW_MS = 15 * 60 * 1000; // 15 minutes
const STORAGE_KEY = 'ovd:radio-skip-quota';

function loadState() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    return JSON.parse(raw) || {};
  } catch {
    return {};
  }
}

function saveState(state) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {}
}

/**
 * Hook returning the user's current skip allowance for `station`.
 *   skipsRemaining — number of skips still available right now
 *   coolDownEndsAt — unix ms timestamp the lock ends, or null if not locked
 *   timeRemaining  — ms until cooldown ends (0 when not locked)
 *   useSkip()      — call when the listener actually skips. Decrements
 *                    counter; when counter hits 0 starts the 15-min timer.
 *
 * Note: VIP listeners bypass this hook entirely (see SyncedRadioPlayer).
 */
export function useNonVipSkipQuota(station) {
  const [state, setState] = useState(() => loadState());
  // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional: mount-only fetch / stable closure
  const stationState = state[station] || { used: 0, lockedUntil: null };

  // Rehydrate cooldown ticker so timeRemaining updates every second
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!stationState.lockedUntil) return;
    const i = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(i);
  }, [stationState.lockedUntil]);

  // Auto-reset quota when cooldown elapses
  useEffect(() => {
    if (!stationState.lockedUntil) return;
    if (now < stationState.lockedUntil) return;
    setState((prev) => {
      const next = { ...prev, [station]: { used: 0, lockedUntil: null } };
      saveState(next);
      return next;
    });
  }, [now, station, stationState.lockedUntil]);

  const skipsRemaining = stationState.lockedUntil ? 0 : Math.max(0, SKIP_QUOTA - stationState.used);
  const timeRemaining = stationState.lockedUntil
    ? Math.max(0, stationState.lockedUntil - now)
    : 0;

  const useSkip = useCallback(() => {
    if (stationState.lockedUntil && Date.now() < stationState.lockedUntil) {
      return { ok: false, reason: 'locked', remaining: stationState.lockedUntil - Date.now() };
    }
    const used = (stationState.used || 0) + 1;
    const lockedUntil = used >= SKIP_QUOTA ? Date.now() + SKIP_WINDOW_MS : null;
    setState((prev) => {
      const next = { ...prev, [station]: { used, lockedUntil } };
      saveState(next);
      return next;
    });
    return {
      ok: true,
      skipsRemaining: Math.max(0, SKIP_QUOTA - used),
      lockedUntil,
    };
  }, [station, stationState]);

  return { skipsRemaining, timeRemaining, useSkip, lockedUntil: stationState.lockedUntil };
}

/** Format a ms duration as "MM:SS" — used for the cooldown banner. */
export function formatCooldown(ms) {
  if (!ms || ms <= 0) return '0:00';
  const total = Math.ceil(ms / 1000);
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
}
